// ============================================================================
// BESPOKIAN Health - Audit Log Page
// Comprehensive audit trail viewer - HIPAA compliance
// ============================================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Filter,
  Download,
  Clock,
  User,
  FileText,
  Shield,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { AuditLogEntry } from '@/types';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge, Spinner } from '@/components/common/ui';
import { useAuth } from '@/contexts/AuthContext';

// ============================================================================
// Types
// ============================================================================

interface AuditFilters {
  userId?: string;
  action?: string;
  entityType?: string;
  startDate?: string;
  endDate?: string;
  searchQuery?: string;
}

// ============================================================================
// API
// ============================================================================

async function getAuditLogs(filters: AuditFilters): Promise<AuditLogEntry[]> {
  const params = new URLSearchParams();
  if (filters.userId) params.append('userId', filters.userId);
  if (filters.action) params.append('action', filters.action);
  if (filters.entityType) params.append('entityType', filters.entityType);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.searchQuery) params.append('q', filters.searchQuery);
  
  return apiClient.get<AuditLogEntry[]>(`/pharmacist/audit?${params.toString()}`);
}

// ============================================================================
// Helper Functions
// ============================================================================

function getActionBadgeVariant(action: string): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' {
  if (action.includes('APPROVE') || action.includes('VERIFY') || action.includes('CHECK')) {
    return 'success';
  }
  if (action.includes('REJECT') || action.includes('CANCEL')) {
    return 'danger';
  }
  if (action.includes('OVERRIDE')) {
    return 'warning';
  }
  if (action.includes('VIEW') || action.includes('SEARCH')) {
    return 'secondary';
  }
  return 'primary';
}

function formatAction(action: string): string {
  return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
}

// ============================================================================
// Component
// ============================================================================

export function AuditLogPage() {
  const { user } = useAuth();
  const isPharmacist = user?.role === 'PHARMACIST';

  // Filter state
  const [filters, setFilters] = useState<AuditFilters>({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);

  // Fetch audit logs
  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: () => getAuditLogs(filters),
    enabled: isPharmacist, // Only pharmacists can view audit logs
  });

  // Export to CSV
  const handleExport = () => {
    if (!logs || logs.length === 0) return;

    const headers = ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'IP Address', 'Details'];
    const rows = logs.map(log => [
      new Date(log.timestamp).toISOString(),
      log.userName,
      log.action,
      log.entityType,
      log.entityId,
      log.ipAddress,
      JSON.stringify(log.details || {}),
    ]);

    const csv = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${filters.startDate}-${filters.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Authorization check
  if (!isPharmacist) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-8 text-center">
            <Shield className="h-12 w-12 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-red-900">Access Restricted</h2>
            <p className="text-red-700 mt-2">Only pharmacists can access the audit log.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-600 mt-1">Complete audit trail for HIPAA compliance</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={!logs || logs.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-between w-full"
          >
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            {showFilters ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
        </CardHeader>
        {showFilters && (
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Action Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action Type
                </label>
                <select
                  value={filters.action || ''}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Actions</option>
                  <option value="VERIFY">Verification</option>
                  <option value="FILL">Fill</option>
                  <option value="CHECK">Pharmacist Check</option>
                  <option value="DUR_OVERRIDE">DUR Override</option>
                  <option value="PDMP_OVERRIDE">PDMP Override</option>
                  <option value="REJECT">Rejection</option>
                  <option value="VIEW">View</option>
                </select>
              </div>

              {/* Entity Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entity Type
                </label>
                <select
                  value={filters.entityType || ''}
                  onChange={(e) => setFilters({ ...filters, entityType: e.target.value || undefined })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Entities</option>
                  <option value="PRESCRIPTION">Prescription</option>
                  <option value="PATIENT">Patient</option>
                  <option value="DUR_ALERT">DUR Alert</option>
                  <option value="PDMP">PDMP</option>
                  <option value="INVENTORY">Inventory</option>
                </select>
              </div>
            </div>

            {/* Search */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Rx number, patient name, or user..."
                  value={filters.searchQuery || ''}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value || undefined })}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button
                variant="ghost"
                onClick={() => setFilters({
                  startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                  endDate: new Date().toISOString().split('T')[0],
                })}
              >
                Reset Filters
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Spinner size="lg" />
        </div>
      ) : logs && logs.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Timestamp</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">User</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Action</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Entity</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">IP Address</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <>
                      <tr
                        key={log.id}
                        className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                        onClick={() => setExpandedEntry(expandedEntry === log.id ? null : log.id)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-400" />
                            <span>{new Date(log.timestamp).toLocaleString()}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span>{log.userName}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={getActionBadgeVariant(log.action)}>
                            {formatAction(log.action)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">{log.entityType}</span>
                            <span className="font-mono text-xs text-gray-500">
                              {log.entityId.slice(0, 8)}...
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs text-gray-500">
                          {log.ipAddress}
                        </td>
                        <td className="py-3 px-4">
                          {expandedEntry === log.id ? (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          )}
                        </td>
                      </tr>
                      {expandedEntry === log.id && log.details && (
                        <tr className="bg-gray-50">
                          <td colSpan={6} className="py-3 px-4">
                            <div className="text-sm">
                              <h4 className="font-medium text-gray-700 mb-2">Details</h4>
                              <pre className="bg-white p-3 rounded border text-xs overflow-x-auto">
                                {JSON.stringify(log.details, null, 2)}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No audit entries</h3>
            <p className="text-gray-600 mt-1">
              No audit log entries found for the selected filters.
            </p>
          </CardContent>
        </Card>
      )}

      {/* HIPAA Notice */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">HIPAA Compliance Notice</h4>
              <p className="text-sm text-blue-700 mt-1">
                This audit log is maintained in compliance with HIPAA regulations. All access to
                protected health information (PHI) is logged and retained for a minimum of 6 years.
                Unauthorized access or disclosure may result in disciplinary action and legal penalties.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
